import { Link, useLocation } from "react-router-dom";
import { Search, Home, BarChart3, Calculator, MessageSquare, Menu, X, ArrowLeft, Sun, Moon } from "lucide-react";
import { Button } from "@/propertyai/components/ui/button";
import { useState } from "react";
import { useTheme } from "@/contexts/ThemeContext";

export const Navbar = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isSearch = location.pathname === '/property-ai/search';
  const { isDark, toggleTheme } = useTheme();

  const navLinks = [
    { to: '/property-ai/search', label: 'Buy', icon: Home },
    { to: '/property-ai/zestimate', label: 'Zestimate', icon: BarChart3 },
    { to: '/property-ai/mortgage-calculator', label: 'Mortgage', icon: Calculator },
    { to: '/property-ai/ai', label: 'AI Advisor', icon: MessageSquare },
  ];

  return (
    <nav className={`sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-sm ${isSearch ? 'shadow-sm' : ''}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14">
          {/* Back to Marketplace + Logo group */}
          <div className="flex items-center gap-3">
            {/* Return to Marketplace button */}
            <Link
              to="/marketplace"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg px-2.5 py-1.5 bg-background hover:bg-secondary transition-colors"
              title="Back to Marketplace"
            >
              <ArrowLeft className="w-3 h-3" />
              <span className="hidden sm:inline">Marketplace</span>
            </Link>

            {/* Logo */}
            <Link to="/property-ai" className="flex items-center gap-2 font-semibold text-lg" data-testid="nav-logo">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Home className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-['Space_Grotesk'] tracking-tight">PropertyAI</span>
            </Link>
          </div>

          {/* Desktop Nav & Theme Toggle */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to}>
                <Button
                  variant={location.pathname === to ? 'secondary' : 'ghost'}
                  size="sm"
                  className="rounded-xl text-sm gap-1.5"
                  data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </Button>
              </Link>
            ))}
            
            <div className="h-6 w-px bg-border mx-1"></div>
            
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl"
              onClick={toggleTheme}
              title="Toggle theme"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl"
              onClick={toggleTheme}
              title="Toggle theme"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="nav-mobile-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-border pt-2">
            {/* Mobile: Return to marketplace */}
            <Link to="/marketplace" onClick={() => setMobileOpen(false)}>
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm hover:bg-secondary/50 text-muted-foreground">
                <ArrowLeft className="w-4 h-4" />
                Back to Marketplace
              </div>
            </Link>
            {navLinks.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} onClick={() => setMobileOpen(false)}>
                <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm ${
                  location.pathname === to ? 'bg-secondary font-medium' : 'hover:bg-secondary/50'
                }`}>
                  <Icon className="w-4 h-4" />
                  {label}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;

